import React, { useState, useRef, useEffect } from 'react';
import { MoveHorizontal } from 'lucide-react';

export const StandardVsDeepSlider = () => {
  const [sliderPos, setSliderPos] = useState(50);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPos((x / rect.width) * 100);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isResizing) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isResizing) return;
    handleMove(e.touches[0].clientX);
  };

  useEffect(() => {
    const handleUp = () => setIsResizing(false);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchend', handleUp);
    return () => {
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
    };
  }, []);

  return (
    <div className="bg-primary/5 rounded-3xl p-6 md:p-10 my-12 border border-primary/10">
      <div className="text-center mb-8">
        <h3 className="text-2xl md:text-3xl font-bold mb-3">Standard vs Deep Cleaning</h3>
        <p className="text-muted-foreground">Drag the slider to see the difference a deep clean makes on hidden buildup.</p>
      </div>

      <div 
        ref={containerRef}
        className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-2xl cursor-col-resize select-none border-4 border-white"
        onMouseDown={() => setIsResizing(true)}
        onTouchStart={() => setIsResizing(true)}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
      >
        {/* After Image (Deep Clean) */}
        <img 
          src="https://vibe.filesafe.space/1776798830316075366/assets/6c8dbe48-e329-42b9-a572-3b1f8b577353.png" 
          alt="Deep Cleaning Result"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          loading="lazy"
          decoding="async"
        />

        {/* Before Image (Standard Clean) */}
        <div 
          className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none"
          style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
        >
          <img 
            src="https://vibe.filesafe.space/1776798830316075366/assets/95a07abb-acad-4810-8b98-e72287a0617a.png" 
            alt="Standard Cleaning Result"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none filter brightness-90"
            loading="lazy"
            decoding="async"
          />
        </div>

        {/* Slider Line */}
        <div 
          className="absolute inset-y-0 w-1 bg-white shadow-lg pointer-events-none"
          style={{ left: `${sliderPos}%` }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center border-4 border-primary">
            <MoveHorizontal className="w-5 h-5 text-primary" />
          </div>
        </div>

        {/* Labels */}
        <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 bg-black/70 backdrop-blur-sm text-white px-3 py-1 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-bold pointer-events-none border border-white/20">
          STANDARD CLEAN
        </div>
        <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 bg-primary text-white px-3 py-1 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-bold pointer-events-none shadow-lg">
          DEEP CLEAN
        </div>
      </div>
    </div>
  );
};
